import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Mail, MessageCircle, ArrowUp } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    const start = window.scrollY;
    const startTime = performance.now();
    const duration = 1200;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, start * (1 - easeOutCubic));

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const socialLinks = [
    { icon: Github, href: "https://github.com/usmaaanahmad", label: "GitHub" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/usmaaanahmad", label: "LinkedIn" },
    { icon: Mail, href: "https://mail.google.com/mail/?view=cm&fs=1&to=usmanahmadasdf8@gmail.com", label: "Email" },
    { icon: MessageCircle, href: "https://wa.me/923143507460", label: "WhatsApp" }
  ];

  return (
    <footer className="relative bg-neutral-950 text-white border-t border-white/10 pt-16 pb-8 overflow-hidden shadow-[0_-10px_30px_rgba(0,0,0,0.5)] flex flex-col min-h-[420px]">
      <div className="container mx-auto px-6">

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 pb-12">

          {}
          <div className="space-y-6">
            <div className="space-y-2 min-h-[88px]">
              <Link to="/" className="hover:opacity-80 transition-opacity inline-block" aria-label="Usman Ahmad - Home">
                <img 
                  src="/logoua.webp" 
                  alt="Usman Ahmad Logo - Full-Stack Developer" 
                  title="Usman Ahmad - Portfolio"
                  className="h-16 w-auto object-contain brightness-0 invert" 
                  width="192" 
                  height="64"
                  loading="lazy"
                />
              </Link>
              <p className="text-xs text-neutral-400 font-bold uppercase tracking-wider leading-[1.6] h-10">
                Full-Stack Developer &<br />UI/UX Architect
              </p>
            </div>

            {}
            <div className="flex items-center gap-3 pt-2 h-12">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-white/20 rounded-full hover:bg-white hover:text-black hover:border-white transition-all active:scale-95"
                  aria-label={link.label}
                >
                  <link.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {}
          <div className="space-y-4">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-neutral-400 border-b border-neutral-900 pb-2 mb-4 leading-none h-6">
              Navigation
            </h4>
            <ul className="space-y-2.5 text-xs font-medium uppercase tracking-wider min-h-[160px]">
              <li><Link to="/" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Home</Link></li>
              <li><Link to="/about" className="hover:text-neutral-400 transition-colors inline-block leading-normal">About</Link></li>
              <li><Link to="/services" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Services</Link></li>
              <li><Link to="/projects" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Projects</Link></li>
              <li><Link to="/blog" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Journal</Link></li>
              <li><Link to="/contact" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Contact</Link></li>
            </ul>
          </div>

          {}
          <div className="space-y-4">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-neutral-400 border-b border-neutral-900 pb-2 mb-4 leading-none h-6">
              Resources & Info
            </h4>
            <ul className="space-y-2.5 text-xs font-medium uppercase tracking-wider min-h-[160px]">
              <li><Link to="/reviews" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Client Reviews</Link></li>
              <li><Link to="/faq" className="hover:text-neutral-400 transition-colors inline-block leading-normal">FAQS Dashboard</Link></li>
              <li><Link to="/uses" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Workspace Setup</Link></li>
            </ul>
          </div>

          {}
          <div className="space-y-4">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-neutral-400 border-b border-neutral-900 pb-2 mb-4 leading-none h-6">
              Legal Policy
            </h4>
            <ul className="space-y-2.5 text-xs font-medium uppercase tracking-wider min-h-[160px]">
              <li><Link to="/privacy" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Terms of Service</Link></li>
              <li><Link to="/cookies" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Cookie Policy</Link></li>
              <li><Link to="/license" className="hover:text-neutral-400 transition-colors inline-block leading-normal">Licenses & Attributions</Link></li>
            </ul>
          </div>

        </div>

        {}
        <div className="border-t border-neutral-900 pt-8 flex flex-col md:flex-row items-center justify-between gap-6 min-h-[64px]">

          <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-4 text-center sm:text-left text-xs font-black uppercase tracking-widest text-neutral-400 min-h-[20px]">
            <span className="inline-block whitespace-nowrap leading-none">© {currentYear} USMAN AHMAD</span>
            <span className="hidden sm:inline text-neutral-800">|</span>
            <span className="inline-block whitespace-nowrap leading-none">BUILT WITH REACT, TAILWIND CSS & SUPABASE</span>
          </div>

          {}
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 border border-neutral-800 hover:border-white hover:text-white px-3 py-1.5 rounded-full transition-all text-xs font-black uppercase tracking-widest active:scale-95 group shrink-0 h-8"
          >
            Back to Top <ArrowUp size={12} className="group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>

      </div>
    </footer>
  );
};

export default Footer;